import { z } from 'zod'

export const CRM_ORG_READ_CONTRACT_VERSION = '1.0.0' as const

const rowSchema = z.record(z.unknown())
const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

export const crmOrgReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  q: optionalQueryString,
  segment: optionalQueryString,
  tier: optionalQueryString,
  page: optionalQueryString,
  page_size: optionalQueryString,
  pageSize: optionalQueryString,
  sort: optionalQueryString,
  limit: optionalQueryString,
  type: optionalQueryString,
}).passthrough()

export const crmOrgReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmOrgListResponseSchema = z.object({
  contract_version: z.literal(CRM_ORG_READ_CONTRACT_VERSION),
  orgs: z.object({
    items: z.array(rowSchema),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }).passthrough(),
  sideEffects: crmOrgReadSideEffectsSchema,
}).passthrough()

export const crmOrgHeaderResponseSchema = z.object({
  contract_version: z.literal(CRM_ORG_READ_CONTRACT_VERSION),
  org: rowSchema.nullable(),
  sideEffects: crmOrgReadSideEffectsSchema,
}).passthrough()

export const crmOrgRosterResponseSchema = z.object({
  contract_version: z.literal(CRM_ORG_READ_CONTRACT_VERSION),
  roster: z.array(rowSchema),
  sideEffects: crmOrgReadSideEffectsSchema,
}).passthrough()

export const crmOrgProgramsResponseSchema = z.object({
  contract_version: z.literal(CRM_ORG_READ_CONTRACT_VERSION),
  programs: z.array(rowSchema),
  sideEffects: crmOrgReadSideEffectsSchema,
}).passthrough()

export const crmOrgCommerceResponseSchema = z.object({
  contract_version: z.literal(CRM_ORG_READ_CONTRACT_VERSION),
  commerce: z.array(rowSchema),
  sideEffects: crmOrgReadSideEffectsSchema,
}).passthrough()

export const crmOrgMembershipsResponseSchema = z.object({
  contract_version: z.literal(CRM_ORG_READ_CONTRACT_VERSION),
  memberships: z.array(rowSchema),
  history: z.array(rowSchema),
  sideEffects: crmOrgReadSideEffectsSchema,
}).passthrough()

export const crmOrgTimelineResponseSchema = z.object({
  contract_version: z.literal(CRM_ORG_READ_CONTRACT_VERSION),
  timeline: z.object({
    events: z.array(rowSchema),
    nextCursor: z.string().nullable(),
  }).passthrough(),
  sideEffects: crmOrgReadSideEffectsSchema,
}).passthrough()

export const crmOrgProjectsResponseSchema = z.object({
  contract_version: z.literal(CRM_ORG_READ_CONTRACT_VERSION),
  projects: z.array(rowSchema),
  sideEffects: crmOrgReadSideEffectsSchema,
}).passthrough()

export const crmOrgIdentityResponseSchema = z.object({
  contract_version: z.literal(CRM_ORG_READ_CONTRACT_VERSION),
  aliases: z.array(rowSchema),
  alternateAttributes: z.array(rowSchema),
  matchCandidates: z.array(rowSchema),
  sideEffects: crmOrgReadSideEffectsSchema,
}).passthrough()

export const crmOrgRecommendationsResponseSchema = z.object({
  contract_version: z.literal(CRM_ORG_READ_CONTRACT_VERSION),
  recommendations: z.array(rowSchema),
  sideEffects: crmOrgReadSideEffectsSchema,
}).passthrough()

export const crmOrgReadErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.string().min(1),
    message: z.string().min(1),
    reference: z.string().min(1),
  }).passthrough(),
}).passthrough()

export type CrmOrgReadQuery = z.infer<typeof crmOrgReadQuerySchema>
export type CrmOrgListResponse = z.infer<typeof crmOrgListResponseSchema>
export type CrmOrgHeaderResponse = z.infer<typeof crmOrgHeaderResponseSchema>
export type CrmOrgRosterResponse = z.infer<typeof crmOrgRosterResponseSchema>
export type CrmOrgProgramsResponse = z.infer<typeof crmOrgProgramsResponseSchema>
export type CrmOrgCommerceResponse = z.infer<typeof crmOrgCommerceResponseSchema>
export type CrmOrgMembershipsResponse = z.infer<typeof crmOrgMembershipsResponseSchema>
export type CrmOrgTimelineResponse = z.infer<typeof crmOrgTimelineResponseSchema>
export type CrmOrgProjectsResponse = z.infer<typeof crmOrgProjectsResponseSchema>
export type CrmOrgIdentityResponse = z.infer<typeof crmOrgIdentityResponseSchema>
export type CrmOrgRecommendationsResponse = z.infer<typeof crmOrgRecommendationsResponseSchema>
export type CrmOrgReadErrorEnvelope = z.infer<typeof crmOrgReadErrorEnvelopeSchema>
